import React, { useEffect, useMemo, useState } from "react";
import { Box, Button, CircularProgress, Typography, Stack, TextField } from "@mui/material";
import Link from "@mui/material/Link";
import BreadcrumbsComponent from "../components/Breadcrumbs";
import SearchInput from "../components/SearchInput";
import { routes } from "../routes/routes";
import useFetchInstruments from "../hooks/useFetchInstruments";
import useImportInstrumentFromExcel from "../hooks/useImportInstrumentFromExcel";
import { ErrorSnackbar } from "../components/Snackbar/ErrorSnackbar";
import { SuccessSnackbar } from "../components/Snackbar/SuccessSnackbar";
import DescriptionIcon from "@mui/icons-material/Description";
import BulkLoadModal from "../components/Modal/BulkLoadModal";
import BulkLoadResultsModal from "../components/Modal/BulkLoadResultsModal";
import EditInstrumentModal from "../components/Modal/EditInstrumentModal";
import useUpdateInstrument from "../hooks/useUpdateInstrument";
import useCreateInstrument from "../hooks/useCreateInstrument";
import { parseBackendErrorMessage } from "../utils/parseBackendError";
import ModalComponent from "../components/Modal/Modal";
import AccordionComponent from "../components/Accordion";
import InstrumentSection from "../components/InstrumentSection";
import { useTheme } from "@mui/material";

const InstrumentsPage = () => {
  const theme = useTheme();
  const { data: instruments, error: responseError, loading, refetch: refetchInstruments } = useFetchInstruments();
  const { importFromExcel, loading: importLoading } = useImportInstrumentFromExcel();
  const [showImportResultModal, setShowImportResultModal] = useState(false);
  const [importResult, setImportResult] = useState(null);

  const [searchQuery, setSearchQuery] = useState("");
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const [showSuccess, setShowSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");

  const { createInstrument } = useCreateInstrument();
  const [showImportModal, setShowImportModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [formData, setFormData] = useState({ instrumentName: "" });

  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedInstrument, setSelectedInstrument] = useState(null);
  const { updateInstrument, error: updateError } = useUpdateInstrument();
  const [expandedInstrumentId, setExpandedInstrumentId] = useState(null);
  const TEMPLATE_URL = `${process.env.PUBLIC_URL || ""}/plantillas/crear_instrumento_plantilla.xlsx`;

  useEffect(() => {
    const err = responseError || updateError;
    if (err) {
      setErrorMessage(err?.message || "Ha ocurrido un error inesperado.");
      setShowError(true);
    }
  }, [responseError, updateError]);

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  const filteredInstruments = useMemo(
    () => (instruments || []).filter((instrument) => (instrument.name || "").toLowerCase().includes(searchQuery.trim().toLowerCase())),
    [instruments, searchQuery],
  );

  const accordionInfo = useMemo(() => {
    if (!filteredInstruments || filteredInstruments.length === 0) return [];
    return filteredInstruments.map((instrument) => ({
      key: instrument.id,
      title: instrument.name,
      details: (
        <Box sx={{ pl: { xs: 2, md: 4 }, py: 1 }}>
          <InstrumentSection
            instrument={instrument}
            isExpanded={expandedInstrumentId === instrument.id}
            onEdit={(inst) => {
              setSelectedInstrument(inst);
              setShowEditModal(true);
            }}
            onAddQuestionnaires={(inst) => {
              setFormData({ instrumentName: inst.name });
              setShowImportModal(true);
            }}
            onQuestionnaireDeleted={async () => {
              await refetchInstruments();
            }}
          />
        </Box>
      ),
    }));
  }, [filteredInstruments, expandedInstrumentId, refetchInstruments]);

  const handleOpenImportModal = () => {
    setShowImportModal(true);
  };

  const handleOpenCreateModal = () => {
    setShowCreateModal(true);
  };

  const onSubmitCreate = async () => {
    const result = await createInstrument({ name: formData.instrumentName });
    if (result.error) {
      setErrorMessage(`No se pudo crear el instrumento. ${result.error.error?.name ? result.error.error.name : ""}`);
      setShowError(true);
      return false;
    }

    setSuccessMessage("El instrumento fue creado correctamente.");
    setShowSuccess(true);
    setShowCreateModal(false);

    handleOpenImportModal();
  };

  const handleCloseCreateModal = () => {
    setShowCreateModal(false);
  };

  const handleChange = (field) => (e) => setFormData((prev) => ({ ...prev, [field]: e.target.value }));

  const handleCloseExcelResultModal = async () => {
    setShowImportModal(false);

    if ((importResult?.created ?? 0) > 0 || (importResult?.updated ?? 0) > 0) {
      await refetchInstruments();
    }

    setImportResult(null);
  };

  const handleSubmitEditInstrument = async (updatedInstrument) => {
    const result = await updateInstrument(updatedInstrument);
    if (!result) {
      setErrorMessage("No se pudo actualizar el instrumento.");
      setShowError(true);
      return;
    }

    setSuccessMessage("El instrumento fue actualizado correctamente.");
    setShowSuccess(true);
    setShowEditModal(false);
    await refetchInstruments();
  };

  const handleImportFromExcel = async (file) => {
    const result = await importFromExcel(file, formData.instrumentName);

    if (result?.error || result?.errorMessage || result?.message) {
      setErrorMessage(
        typeof result?.errorMessage?.message === "string"
          ? result.errorMessage.error
          : typeof result?.errorMessage === "string"
            ? result.errorMessage
            : typeof result?.message === "string"
              ? result.message
              : typeof result?.error === "string"
                ? result.error
                : "Error desconocido.",
      );
      setShowError(true);
    } else {
      setImportResult(result.data);
      setShowImportResultModal(true);
      setShowImportModal(false);
    }
  };

  return (
    <Box p={3}>
      <BreadcrumbsComponent
        breadcrumbs={[
          <Link underline="none" key="1" color="inherit" href={routes.root}>
            Página Principal
          </Link>,
          <Typography key="2" color="secondary" sx={{ fontSize: "14px" }}>
            Instrumentos
          </Typography>,
        ]}
      />

      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, color: "#333333", mt: 2 }}>
          Listado de instrumentos
        </Typography>

        <Box>
          <Button variant="contained" component="span" sx={{ mr: 2 }} disabled={importLoading} onClick={handleOpenCreateModal}>
            {importLoading ? "Importando..." : "Importar Instrumento"}
          </Button>
        </Box>
      </Box>

      <Box sx={{ mt: 2, mb: 3 }}>
        <SearchInput id="search-instruments" placeholder="Buscar por nombre" value={searchQuery} onChange={handleSearchChange} />
      </Box>

      <Box
        sx={{
          maxWidth: 1200,
          mx: "auto",
          p: { xs: 2, md: 4 },
          bgcolor: "background.paper",
          borderRadius: 2,
          boxShadow: theme.shadows[1],
        }}
      >
        <Box
          sx={{
            "& .MuiAccordion-root": {
              mt: 1.5,
              borderRadius: "4px",
              overflow: "hidden",
            },
          }}
        >
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress />
            </Box>
          ) : filteredInstruments.length === 0 ? (
            <Typography align="center" color="textSecondary">
              No hay instrumentos
            </Typography>
          ) : (
            <AccordionComponent accordionInfo={accordionInfo} expandedAppId={expandedInstrumentId} onChange={(id) => setExpandedInstrumentId(id)} />
          )}
        </Box>
      </Box>

      <ErrorSnackbar open={showError} onClose={() => setShowError(false)} title="Error" message={errorMessage} />
      <SuccessSnackbar
        open={showSuccess}
        onClose={() => setShowSuccess(false)}
        title="Completado"
        message={successMessage || "Operación realizada correctamente."}
      />

      {/* Modal de resultados de importación */}
      <BulkLoadResultsModal
        open={showImportResultModal}
        onClose={() => {
          setShowImportResultModal(false);
          handleCloseExcelResultModal();
        }}
        result={importResult}
        title="Resultado de importación de cuestionarios del instrumento"
        labels={{
          total: "Total cargados",
          created: "Creados",
          updated: "Actualizados",
          failed: "No creados",
          skipped: "Sin cambios",
        }}
        keys={{
          total: "total",
          created: "created",
          updated: "updated",
          failed: "failed",
          errorDetails: "error_details",
          skipped: "skipped",
        }}
        parseErrorMessage={parseBackendErrorMessage}
      />

      {/* Modal para importar instrumento (grupo de cuestionarios) por Excel */}
      <BulkLoadModal
        open={showImportModal}
        onClose={() => setShowImportModal(false)}
        onSelectFile={handleImportFromExcel}
        title="Importar cuestionarios del instrumento por Excel"
        description={`Importando cuestionarios para el instrumento: ${formData.instrumentName}`}
        template={{ url: TEMPLATE_URL, label: "Descargar plantilla" }}
        accept=".xlsx,.xls"
        maxSizeMB={10}
        uploadButtonLabel="Subir y crear"
        icon={<DescriptionIcon fontSize="large" color="tertiary" />}
        warnings={["Todos los campos son obligatorios a excepción de 'regla' y 'flags'"]}
        closeOnSuccess={false}
        isLoading={importLoading}
      />

      <ModalComponent open={showCreateModal} onClose={handleCloseCreateModal} width="600px">
        <Box sx={{ p: 4 }}>
          {/* Encabezado */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
            <Typography variant="h6" fontWeight={600} sx={{ lineHeight: 1.2 }}>
              Creación de nuevo instrumento
            </Typography>
          </Box>

          {/* Descripción */}
          <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
            Escriba el nombre del instrumento a crear.
          </Typography>

          {/* Campo de entrada */}
          <TextField
            inputProps={{ maxLength: 128 }}
            size="small"
            label="Nombre del instrumento"
            fullWidth
            variant="outlined"
            value={formData.instrumentName}
            onChange={handleChange("instrumentName")}
            type="text"
            sx={{ mb: 3 }}
          />

          {/* Botones de acción */}
          <Stack direction="row" spacing={2} sx={{ justifyContent: "flex-end" }}>
            <Button variant="outlined" onClick={handleCloseCreateModal} sx={{ minHeight: 40 }}>
              Cancelar
            </Button>
            <Button variant="contained" onClick={onSubmitCreate} sx={{ minHeight: 40 }}>
              Crear Instrumento
            </Button>
          </Stack>
        </Box>
      </ModalComponent>

      <EditInstrumentModal
        open={showEditModal}
        onClose={() => setShowEditModal(false)}
        initialData={{
          id: selectedInstrument?.id,
          name: selectedInstrument?.name,
        }}
        onSubmit={handleSubmitEditInstrument}
      />
    </Box>
  );
};

export default InstrumentsPage;
